import { useEffect } from 'react';
import type { LottieRef } from 'lottie-react';

/** Pauses a looping Lottie while off-screen (100px pre-roll); no-op without IntersectionObserver. */
export function useLottieOffscreenPause(
  lottieRef: LottieRef,
  animationData: unknown,
  enabled = true
): void {
  useEffect(() => {
    if (!enabled || !animationData || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    // Set by lottie-react's own mount effect, which runs before this parent effect.
    const container = lottieRef.current?.animationContainerRef.current;
    if (!container) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // One target, entries oldest-first: only the newest reflects current visibility.
        if (entries[entries.length - 1].isIntersecting) lottieRef.current?.play();
        else lottieRef.current?.pause();
      },
      { rootMargin: '100px' }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [lottieRef, animationData, enabled]);
}
